/** @jsxImportSource @emotion/react */
import { Fragment, useMemo, useState } from "react";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { IAppState } from "../../../core/redux/IAppState";
import { getResource } from "../../../language/resources/getResource";
import expandIcon from "../../../assets/icons/circlePlus.svg";
import collapseIcon from "../../../assets/icons/circleMinus.svg";
import {
  COOL_BLUE,
  COOLER_GREY,
  DARK_BLUE_CREEK,
  DARK_GREY,
  DARK_RED_CREEK,
  IKON_BLUE,
  LIGHT_GREY,
  MUTED_GREY,
  WHITE,
} from "../../../styles/colors";
import { StyledBoxedMessage } from "../../../forms/elements/boxedMessage/StyledBoxedMessage";
import { BoxedErrorInverse } from "../../../forms/elements/boxedMessage/boxedMessageStyles";
import { AccessRuleChangeSet } from "../models";
import { LiftAccessRuleSet, liftAccessRuleSetKey } from "./models";
import type { BulkChangeModes } from "./LiftAccessRuleSetBulkEditor";
import type { ChangeScan } from "./bulkEditChangeScan";
import {
  CHANGE_COLUMNS,
  IDENTITY_CARD_WIDTH,
  IDENTITY_COLUMNS,
  cellCtx,
  resolveChangedCell,
} from "./bulkEditChangeScan";
import {
  MISSING,
  SHARED_MARK,
  dateRangeText,
  shortDay,
  timeRangeText,
} from "./ruleSetDisplay";

type CategoryKey = keyof BulkChangeModes;

type Resources = Record<string, any>;

/** One "• Set …" / "+ Add …" / "− Remove …" line under a category heading. */
type ChangeLine = { symbol: string; text: string; destructive?: boolean };

type ChangeGroup = {
  category: CategoryKey;
  labelKey: string;
  lines: ChangeLine[];
  /** True when the mode deletes existing rules in the category first. */
  replaceNote: boolean;
};

/**
 * The edit as prose, applied to every selected rule set. Pure, so it can be tested
 * without the view. Describes intent, not per-set outcomes — which sets actually
 * change comes from the scan.
 */
export function buildChangeGroups(
  changeSet: AccessRuleChangeSet,
  modes: BulkChangeModes,
  resources: Resources,
  dateFormat: string,
  timeFormat: string,
): ChangeGroup[] {
  const groups: ChangeGroup[] = [];

  const clearLine = (textKey: string): ChangeLine => ({
    symbol: "−",
    text: getResource(resources, textKey),
    destructive: true,
  });

  const effectiveRule = changeSet.effectiveDateRange.created[0];

  if (
    modes.effectiveDate === "replace" &&
    effectiveRule?.effectiveDateStart &&
    effectiveRule?.effectiveDateEnd
  ) {
    groups.push({
      category: "effectiveDate",
      labelKey: "ACCESS_RULE_BULK_CATEGORY_EFFECTIVE_DATE",
      replaceNote: false,
      lines: [
        {
          symbol: "•",
          text: getResource(
            resources,
            "ACCESS_RULE_BULK_SUMMARY_SET_EFFECTIVE_DATES",
            dateRangeText(
              effectiveRule.effectiveDateStart,
              effectiveRule.effectiveDateEnd,
              dateFormat,
            ),
          ),
        },
      ],
    });
  }

  if (
    modes.effectiveTime === "replace" &&
    effectiveRule?.effectiveTimeSlotStart &&
    effectiveRule?.effectiveTimeSlotEnd
  ) {
    groups.push({
      category: "effectiveTime",
      labelKey: "ACCESS_RULE_BULK_CATEGORY_EFFECTIVE_TIME",
      replaceNote: false,
      lines: [
        {
          symbol: "•",
          text: getResource(
            resources,
            "ACCESS_RULE_BULK_SUMMARY_SET_EFFECTIVE_TIME",
            timeRangeText(
              effectiveRule.effectiveTimeSlotStart,
              effectiveRule.effectiveTimeSlotEnd,
              timeFormat,
            ),
          ),
        },
      ],
    });
  }

  if (modes.maxDays === "clear") {
    groups.push({
      category: "maxDays",
      labelKey: "ACCESS_RULE_BULK_CATEGORY_MAX_DAYS",
      replaceNote: false,
      lines: [clearLine("ACCESS_RULE_BULK_SUMMARY_CLEAR_MAX_DAYS")],
    });
  } else if (modes.maxDays === "replace") {
    const rule = changeSet.maxDays.created[0];
    if (rule && rule.numberOfDays != null) {
      groups.push({
        category: "maxDays",
        labelKey: "ACCESS_RULE_BULK_CATEGORY_MAX_DAYS",
        replaceNote: false,
        lines: [
          {
            symbol: "•",
            text: getResource(
              resources,
              rule.sharedUsage
                ? "ACCESS_RULE_BULK_SUMMARY_SET_MAX_DAYS_SHARED_ADDED"
                : "ACCESS_RULE_BULK_SUMMARY_SET_MAX_DAYS_SHARED_REMOVED",
              rule.numberOfDays,
            ),
          },
        ],
      });
    }
  }

  if (modes.blackoutDateRange === "clear") {
    groups.push({
      category: "blackoutDateRange",
      labelKey: "ACCESS_RULE_BULK_CATEGORY_BLACKOUT_RANGE",
      replaceNote: false,
      lines: [clearLine("ACCESS_RULE_BULK_SUMMARY_CLEAR_BLACKOUT_RANGE")],
    });
  } else if (modes.blackoutDateRange !== "unchanged") {
    const lines: ChangeLine[] = changeSet.blackoutDateRange.created.map((r) => ({
      symbol: "+",
      text: getResource(
        resources,
        "ACCESS_RULE_BULK_SUMMARY_ADD_BLACKOUT_RANGE",
        // Both return undefined on an incomplete rule, which would render literally.
        dateRangeText(r.blackoutDateStart, r.blackoutDateEnd, dateFormat) ??
          MISSING,
        timeRangeText(
          r.blackoutTimeSlotStart,
          r.blackoutTimeSlotEnd,
          timeFormat,
        ) ?? MISSING,
      ),
    }));
    if (lines.length > 0) {
      groups.push({
        category: "blackoutDateRange",
        labelKey: "ACCESS_RULE_BULK_CATEGORY_BLACKOUT_RANGE",
        replaceNote: modes.blackoutDateRange === "replace",
        lines,
      });
    }
  }

  if (modes.blackoutDayOfWeek === "clear") {
    groups.push({
      category: "blackoutDayOfWeek",
      labelKey: "ACCESS_RULE_BULK_CATEGORY_BLACKOUT_DOW",
      replaceNote: false,
      lines: [clearLine("ACCESS_RULE_BULK_SUMMARY_CLEAR_BLACKOUT_DOW")],
    });
  } else if (modes.blackoutDayOfWeek !== "unchanged") {
    const lines: ChangeLine[] = changeSet.blackoutDayOfWeek.created.map((r) => ({
      symbol: "+",
      text: getResource(
        resources,
        "ACCESS_RULE_BULK_SUMMARY_ADD_BLACKOUT_DOW",
        shortDay(r.dayOfWeekId, resources),
        timeRangeText(
          r.blackoutTimeSlotStart,
          r.blackoutTimeSlotEnd,
          timeFormat,
        ) ?? MISSING,
      ),
    }));
    if (lines.length > 0) {
      groups.push({
        category: "blackoutDayOfWeek",
        labelKey: "ACCESS_RULE_BULK_CATEGORY_BLACKOUT_DOW",
        replaceNote: modes.blackoutDayOfWeek === "replace",
        lines,
      });
    }
  }

  return groups;
}

const rootCss = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const questionCss = css`
  font-size: 15px;
  color: ${IKON_BLUE};
`;

const noChangeBannerCss = css`
  padding: 10px 14px;
  border: 1px solid ${COOLER_GREY};
  border-radius: 6px;
  background: ${LIGHT_GREY};
  color: ${DARK_GREY};
  font-size: 14px;
  font-weight: 600;
`;

const unaffectedNoteCss = css`
  margin-top: 6px;
  font-size: 12px;
  color: ${MUTED_GREY};
`;

// Applied while the grid is open, so it inherits the height its container has left
// rather than growing the dialog. Collapsed, the section keeps its natural height.
const fillCss = css`
  flex: 1 1 auto;
  min-height: 0;
`;

const gridSectionCss = css`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  overflow: hidden;
`;

const collapseToggleCss = css`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  width: auto;
  padding: 0;
  border: none;
  background: none;
  color: ${IKON_BLUE};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  .expand-icon {
    display: inline-block;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }

  /* Caption beside the label — "Applies to every selected rule set". */
  .applies {
    color: ${MUTED_GREY};
    font-size: 12px;
    font-weight: 400;
  }
`;

// The legend sits in this row, not inside the button — it's a caption, not a control.
const affectedHeaderCss = css`
  display: flex;
  align-items: center;
  gap: 28px;

  .legend {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    color: ${DARK_GREY};
    font-size: 12px;
    font-weight: 400;
  }

  .legend .swatch {
    display: inline-block;
    box-sizing: border-box;

    /* Wide and filled, so it reads as a changed cell rather than a checkbox. */
    width: 26px;
    height: 13px;
    background: ${COOL_BLUE};
  }

  .legend .legend-arrow {
    font-weight: 700;

    /* Matches the thickened → used inside changed cells. */
    -webkit-text-stroke: 0.6px currentcolor;
  }
`;

// Quieter than the blue expand toggle beside it — a view option, not the control.
const showAllColumnsCss = css`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
  color: ${DARK_GREY};
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  user-select: none;

  input {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    margin: 0;
    cursor: pointer;
  }

  span {
    line-height: 1;
  }
`;

// Applied while the grid is collapsed, when there are no columns to show yet.
const showAllColumnsDisabledCss = css`
  cursor: default;
  opacity: 0.5;

  input {
    cursor: default;
  }
`;

// The only thing in the dialog that scrolls: it takes the spare height so the dialog
// never has to. The floor keeps a row or two visible in a short window.
const gridScrollCss = css`
  flex: 1 1 auto;
  min-height: min(140px, 20vh);
  margin-top: 6px;
  overflow: auto;
`;

const gridTableCss = css`
  width: 100%;
  border-spacing: 0;
  border-collapse: separate;

  /* Columns from the header widths, not row content — required with
     content-visibility, else off-screen rows re-size columns while scrolling. */
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    /* Required: column widths include padding, so content-box cells overflow. */
    box-sizing: border-box;
    padding: 5px 8px;
    line-height: 1.35;
    text-align: left;
    vertical-align: top;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${WHITE};
    color: ${IKON_BLUE};
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
  }

  /* The outline is drawn by the body cells, so it encloses the rows only and leaves
     the sticky headers open. */
  tbody td {
    border-bottom: 1px solid ${COOLER_GREY};
  }

  tbody tr:first-of-type td {
    border-top: 1px solid ${COOLER_GREY};
  }

  tbody td:first-of-type {
    border-left: 1px solid ${COOLER_GREY};
  }

  /* Without this, adjacent changed cells' blue fills merge. */
  tbody td + td {
    border-left: 1px solid ${COOLER_GREY};
  }

  tbody td:last-of-type {
    border-right: 1px solid ${COOLER_GREY};
  }

  /* Skips layout/paint for off-screen rows so a large selection doesn't stall on
     expand. The placeholder height must be near the real median (rows run
     90–250px) or the scrollbar jumps as rows are measured. */
  tbody tr {
    content-visibility: auto;
    contain-intrinsic-size: auto 120px;
  }

  .col-center {
    text-align: center;
  }

  /* Identity fields as one card, two label/value pairs per row. */
  .identity {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto minmax(0, 1fr);
    gap: 6px 14px;
    align-content: start;
    margin: 0;
    padding: 12px 16px;
    border: 1px solid ${COOLER_GREY};
    border-radius: 6px;
    background: ${LIGHT_GREY};
    font-size: 12px;
  }

  .identity dt {
    font-weight: 700;
    white-space: nowrap;
  }

  .identity dd {
    min-width: 0;
    margin: 0;
    overflow-wrap: anywhere;
  }

  .plain {
    color: ${IKON_BLUE};
    white-space: pre-line;
  }

  /* A changing column this set already matches: grey, the "before" ink. */
  td.no-change {
    color: ${DARK_GREY};
  }

  /* Shared-usage asterisk, matching the main Access Overview grid. */
  .shared-star {
    margin-left: 1px;
    color: ${DARK_BLUE_CREEK};
    font-size: 15px;
    font-weight: 700;
    cursor: help;
  }

  /* Filled, so the whole cell reads as changed rather than just its text. */
  td.changed {
    background: ${COOL_BLUE};
  }

  .change-cell {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 6px;
  }

  /* Hidden visually; carries "changed" for assistive tech, which can't see the fill. */
  .change-cell .sr-only {
    position: absolute;
    clip-path: inset(50%);
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    border: 0;
    overflow: hidden;
    white-space: nowrap;
  }

  .change-cell .before {
    color: ${DARK_GREY};
    white-space: pre-line;
  }

  .change-cell .arrow {
    align-self: center;
    color: ${DARK_BLUE_CREEK};
    font-size: 16px;
    font-weight: 700;
    line-height: 1;

    /* Thickens the → to sit between the thin arrow and the heavy glyph. */
    -webkit-text-stroke: 0.8px ${DARK_BLUE_CREEK};
  }

  .change-cell .after {
    color: ${IKON_BLUE};
    font-weight: 700;
    white-space: pre-line;
  }
`;

// Boxed to match the other save and delete confirmations; this only adds spacing.
const dataChangeNoticeCss = css`
  flex-shrink: 0;
  margin: 0 0 12px;
`;

const changePanelCss = css`
  margin-top: 8px;
  border: 1px solid ${COOLER_GREY};
  border-radius: 6px;
  padding: 4px 0;
`;

const changeGroupCss = css`
  padding: 10px 16px;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${LIGHT_GREY};
  }

  .group-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: normal;
    text-transform: uppercase;
    color: ${DARK_GREY};
    margin-bottom: 6px;
  }

  .change-line {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 1px 0;
    font-size: 14px;
    color: ${IKON_BLUE};
  }

  .change-line .symbol {
    flex: 0 0 auto;
    width: 12px;
    color: ${DARK_BLUE_CREEK};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    text-align: center;
  }

  .change-line.is-destructive {
    color: ${DARK_RED_CREEK};
    font-weight: 600;
  }

  .change-line.is-destructive .symbol {
    color: ${DARK_RED_CREEK};
  }

  .change-line.is-neutral {
    color: ${DARK_GREY};
    font-style: italic;
  }

  .change-line.is-neutral .symbol {
    color: ${DARK_GREY};
    font-style: normal;
  }

  .replace-note {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 4px;
    font-size: 12px;
    font-weight: 600;
    color: ${DARK_RED_CREEK};
  }

  .replace-note .symbol {
    flex: 0 0 auto;
    width: 12px;
    text-align: center;
  }
`;

/** Turns a trailing shared-usage marker into a styled asterisk with a tooltip. */
function renderValueLines(text: string, resources: Resources) {
  const sharedTitle = getResource(resources, "LIFT_ACCESS_RULES_TABLE_SHARED");
  return text.split("\n").map((line, i) => {
    const shared = line.endsWith(SHARED_MARK);
    const base = shared ? line.slice(0, -SHARED_MARK.length) : line;
    return (
      <Fragment key={i}>
        {i > 0 && <br />}
        {base}
        {shared && (
          <span className="shared-star" title={sharedTitle}>
            {SHARED_MARK}
          </span>
        )}
      </Fragment>
    );
  });
}

// Thickens any "→" to match the arrow in changed cells, so the legend copy can stay
// in one resource string instead of being split around a hard-coded glyph.
function withThickArrow(text: string) {
  return text.split("→").map((part, i) => (
    <Fragment key={i}>
      {i > 0 && <span className="legend-arrow">→</span>}
      {part}
    </Fragment>
  ));
}

function ChangeCell({
  beforeText,
  afterText,
  resources,
}: {
  beforeText: string;
  afterText: string;
  resources: Resources;
}) {
  // A placeholder when there was no prior value, so an addition still reads as
  // before → after rather than being distinguishable only by the fill.
  const displayBefore = beforeText.length > 0 ? beforeText : MISSING;
  return (
    <div className="change-cell">
      <span className="sr-only">
        {getResource(resources, "ACCESS_RULE_BULK_SUMMARY_CHANGED_SR")}
      </span>
      <span className="before">
        {renderValueLines(displayBefore, resources)}
      </span>
      <span className="arrow" aria-hidden="true">
        →
      </span>
      <span className="after">{renderValueLines(afterText, resources)}</span>
    </div>
  );
}

type Props = {
  ruleSets: LiftAccessRuleSet[];
  changeSet: AccessRuleChangeSet;
  modes: BulkChangeModes;
  activeCategories: CategoryKey[];
  /** Passed in, not computed here — the editor needs the same answers. */
  scan: ChangeScan;
  /**
   * Grid expansion: controlled when both are passed, so the state can outlive this
   * component being remounted by a dock / pop-out. Local otherwise.
   */
  gridOpen?: boolean;
  onGridOpenChange?: (open: boolean) => void;
  /** Column visibility, same arrangement as gridOpen. */
  showAllColumns?: boolean;
  onShowAllColumnsChange?: (show: boolean) => void;
};

export function BulkEditChangeSummary({
  ruleSets,
  changeSet,
  modes,
  activeCategories,
  scan,
  gridOpen,
  onGridOpenChange,
  showAllColumns,
  onShowAllColumnsChange,
}: Props) {
  const resources = useSelector((s: IAppState) => s.locale);
  const dateFormat = useSelector((s: IAppState) => s.defaultDateFormat);
  const timeFormat = useSelector((s: IAppState) => s.defaultTimeFormat);

  const groups = useMemo(
    () => buildChangeGroups(changeSet, modes, resources, dateFormat, timeFormat),
    [changeSet, modes, resources, dateFormat, timeFormat],
  );

  const { changedCategories, unaffectedCount } = scan;

  const nothingChanges =
    activeCategories.length > 0 && changedCategories.size === 0;

  // Collapsed on arrival so a large selection doesn't bury the summary above it.
  const [localGridOpen, setLocalGridOpen] = useState(false);
  const isGridOpen = gridOpen ?? localGridOpen;
  const setGridOpen = (open: boolean) =>
    onGridOpenChange ? onGridOpenChange(open) : setLocalGridOpen(open);

  const [isSummaryOpen, setSummaryOpen] = useState(true);

  // Off by default: the untouched columns are context, the changing ones the review.
  const [localShowAllColumns, setLocalShowAllColumns] = useState(false);
  const isShowAllColumns = showAllColumns ?? localShowAllColumns;
  const setShowAllColumns = (show: boolean) =>
    onShowAllColumnsChange
      ? onShowAllColumnsChange(show)
      : setLocalShowAllColumns(show);

  // Closing clears the column choice: the checkbox is unreachable while closed, and it

  // still drives the dialog width.
  const toggleGrid = () => {
    const open = !isGridOpen;
    setGridOpen(open);
    if (!open) setShowAllColumns(false);
  };

  const changing = useMemo(() => new Set(activeCategories), [activeCategories]);
  const visibleColumns = useMemo(
    () =>
      isShowAllColumns
        ? CHANGE_COLUMNS
        : CHANGE_COLUMNS.filter((col) => changing.has(col.modeKey)),
    [changing, isShowAllColumns],
  );

  // As the table's min-width, makes a narrow dialog scroll rather than squeeze.
  const gridMinWidth =
    IDENTITY_CARD_WIDTH +
    visibleColumns.reduce((total, col) => total + col.minWidth, 0);

  const ctx = useMemo(
    () => cellCtx({ changeSet, modes, resources, dateFormat, timeFormat }),
    [changeSet, modes, resources, dateFormat, timeFormat],
  );

  return (
    <div css={[rootCss, isGridOpen && fillCss]}>
      {/* The commit question: this step is the last point before data is written. */}
      <div css={questionCss}>
        {getResource(resources, "ACCESS_RULE_BULK_EDIT_SAVE_CONFIRMATION")}
      </div>

      <StyledBoxedMessage
        message={getResource(resources, "ACCESS_RULE_DATA_CHANGE_WARNING")}
        boxStyle={BoxedErrorInverse}
        extraCss={dataChangeNoticeCss}
      />

      {nothingChanges && (
        <div css={noChangeBannerCss}>
          {getResource(resources, "ACCESS_RULE_BULK_SUMMARY_NO_CHANGE_ALL")}
        </div>
      )}

      <div>
        <button
          type="button"
          css={collapseToggleCss}
          aria-expanded={isSummaryOpen}
          aria-controls="bulk-edit-change-summary"
          onClick={() => setSummaryOpen(!isSummaryOpen)}
        >
          <img
            className="expand-icon"
            src={isSummaryOpen ? collapseIcon : expandIcon}
            alt=""
          />
          <span>
            {getResource(resources, "ACCESS_RULE_BULK_SUMMARY_HEADING")}
          </span>
          <span className="applies">
            {getResource(resources, "ACCESS_RULE_BULK_SUMMARY_APPLIES")}
          </span>
        </button>

        {isSummaryOpen && (
          <div id="bulk-edit-change-summary" css={changePanelCss}>
            {groups.map((group) => {
              const noChange = !changedCategories.has(group.category);
              return (
                <div key={group.category} css={changeGroupCss}>
                  <div className="group-title">
                    {getResource(resources, group.labelKey)}
                  </div>
                  {noChange ? (
                    <div className="change-line is-neutral">
                      <span className="symbol">=</span>
                      <span>
                        {getResource(
                          resources,
                          "ACCESS_RULE_BULK_SUMMARY_NO_CHANGE_CATEGORY",
                        )}
                      </span>
                    </div>
                  ) : (
                    <>
                      {group.lines.map((line, idx) => (
                        <div
                          key={idx}
                          className={
                            line.destructive
                              ? "change-line is-destructive"
                              : "change-line"
                          }
                        >
                          <span className="symbol">{line.symbol}</span>
                          <span>{line.text}</span>
                        </div>
                      ))}
                      {group.replaceNote && (
                        <div className="replace-note">
                          <span className="symbol">−</span>
                          <span>
                            {getResource(
                              resources,
                              "ACCESS_RULE_BULK_SUMMARY_REPLACE_NOTE",
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div css={[gridSectionCss, isGridOpen && fillCss]}>
        <div css={affectedHeaderCss}>
          <button
            type="button"
            css={collapseToggleCss}
            aria-expanded={isGridOpen}
            aria-controls="bulk-edit-affected-grid"
            onClick={toggleGrid}
          >
            <img
              className="expand-icon"
              src={isGridOpen ? collapseIcon : expandIcon}
              alt=""
            />
            <span>
              {getResource(
                resources,
                "ACCESS_RULE_BULK_SUMMARY_AFFECTED_TOGGLE",
                ruleSets.length,
              )}
            </span>
          </button>

          <label
            css={[showAllColumnsCss, !isGridOpen && showAllColumnsDisabledCss]}
          >
            <input
              type="checkbox"
              checked={isShowAllColumns}
              // Only actionable once there is a grid to widen.
              disabled={!isGridOpen}
              onChange={(e) => setShowAllColumns(e.target.checked)}
            />
            <span>
              {getResource(resources, "ACCESS_RULE_SUMMARY_SHOW_ALL_COLUMNS")}
            </span>
          </label>

          <span className="legend">
            <span className="swatch" />
            {withThickArrow(
              getResource(resources, "ACCESS_RULE_BULK_SUMMARY_CHANGING_LEGEND"),
            )}
          </span>
        </div>

        {/* Suppressed on a whole-edit no-op — the banner already said it. */}
        {!nothingChanges && unaffectedCount > 0 && (
          <div css={unaffectedNoteCss}>
            {getResource(
              resources,
              "ACCESS_RULE_BULK_SUMMARY_UNAFFECTED",
              unaffectedCount,
              ruleSets.length,
            )}
          </div>
        )}

        {isGridOpen && (
          <div id="bulk-edit-affected-grid" css={gridScrollCss}>
            <table css={gridTableCss} style={{ minWidth: gridMinWidth }}>
              <thead>
                <tr>
                  {/* No width: the one auto column, so it absorbs any spare width
                      rather than the value-sized change columns. */}
                  <th scope="col">
                    {getResource(
                      resources,
                      "ACCESS_RULE_BULK_SUMMARY_RULE_SET_COLUMN",
                    )}
                  </th>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={
                        col.align === "center" ? "col-center" : undefined
                      }
                      style={{ width: col.minWidth }}
                    >
                      {getResource(resources, col.labelKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ruleSets.map((rs) => {
                  const key = liftAccessRuleSetKey(rs);
                  const scanRow = scan.rows.get(key);
                  return (
                    <tr key={key}>
                      <td>
                        <dl className="identity">
                          {IDENTITY_COLUMNS.map((col) => (
                            <Fragment key={col.key}>
                              <dt>{getResource(resources, col.labelKey)}:</dt>
                              <dd>{col.current(rs).join(", ") || MISSING}</dd>
                            </Fragment>
                          ))}
                        </dl>
                      </td>
                      {visibleColumns.map((col) => {
                        const centerClass =
                          col.align === "center" ? "col-center" : "";
                        // On demand if this set wasn't scanned, rather than showing
                        // another row's values.
                        const { beforeText, afterText, changed } =
                          scanRow?.cells[col.key] ??
                          resolveChangedCell(col, ctx, rs, resources);

                        if (changed) {
                          return (
                            <td
                              key={col.key}
                              className={[centerClass, "changed"]
                                .filter(Boolean)
                                .join(" ")}
                            >
                              <ChangeCell
                                beforeText={beforeText}
                                afterText={afterText}
                                resources={resources}
                              />
                            </td>
                          );
                        }
                        // Flagged as a no-op: the column is only on screen because
                        // the category changes for some other set.
                        return (
                          <td
                            key={col.key}
                            className={[centerClass, "plain", "no-change"]
                              .filter(Boolean)
                              .join(" ")}
                            title={getResource(
                              resources,
                              "ACCESS_RULE_BULK_SUMMARY_NO_CHANGE_CELL",
                            )}
                          >
                            {renderValueLines(beforeText, resources)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
